import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap, Loader2, ArrowLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La password deve avere almeno 8 caratteri.");
    if (password !== confirm) return setError("Le password non coincidono.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password aggiornata. Ora puoi accedere.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F6F8] px-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-lg font-bold text-slate-900">ESSECI E-LIGHT</span>
        </div>

        <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight">Nuova password</h2>
        <p className="mt-1 text-sm text-slate-500">Imposta una nuova password per il tuo account.</p>

        {!token && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="reset-no-token">
            Link non valido. Richiedi un nuovo link di reimpostazione.
          </div>
        )}
        {error && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="reset-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="password">Nuova password</Label>
            <Input id="password" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Almeno 8 caratteri"
              data-testid="reset-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Conferma password</Label>
            <Input id="confirm" type="password" required value={confirm}
              onChange={(e) => setConfirm(e.target.value)} placeholder="Ripeti la password"
              data-testid="reset-confirm-input" />
          </div>
          <Button type="submit" disabled={loading || !token} data-testid="reset-submit-button"
            className="w-full">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiorna password"}
          </Button>
        </form>

        <div className="mt-5 text-center">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:text-brand-strong" data-testid="back-to-login">
            <ArrowLeft className="h-4 w-4" /> Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
